(() => {
  const GIFT_PROPERTY = '_kuuraii_reward_gift';

  let syncTimer;
  let activeSync = null;
  let checkoutPreflightRunning = false;
  let replayingCheckout = false;

  function getDrawer() {
    return document.querySelector('cart-drawer');
  }

  const retiredGiftVariants = new Set([47619508273315, 47619512467619, 47619513778339, 47827344097443]);

  function isRetiredGift(item) {
    return Boolean(item && (
      retiredGiftVariants.has(Number(item.variant_id)) ||
      (item.properties && String(item.properties[GIFT_PROPERTY]) === 'true')
    ));
  }

  function requestJson(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }).then((response) => {
      if (!response.ok) throw new Error(`Cart request failed with ${response.status}`);
      return response.json();
    });
  }

  function fetchCart() {
    return fetch(`${window.Shopify.routes.root}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    }).then((response) => {
      if (!response.ok) throw new Error('Cart fetch failed');
      return response.json();
    });
  }

  function sectionRequest() {
    return {
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    };
  }

  function renderCart(response) {
    if (!response || !response.sections) return;
    const drawer = getDrawer();
    if (drawer && typeof drawer.renderContents === 'function') drawer.renderContents(response);
  }

  function showCartMessage(message) {
    const error = document.getElementById('CartDrawer-CartErrors');
    if (error) error.textContent = message || '';
  }

  function productHandle(item) {
    if (item && item.handle) return String(item.handle);
    const match = item && item.url && String(item.url).match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  async function unavailableCartItems(cart) {
    const items = (cart && cart.items) || [];
    const productCache = new Map();

    const checks = await Promise.all(
      items.map(async (item) => {
        const handle = productHandle(item);
        if (!handle) return null;

        try {
          if (!productCache.has(handle)) {
            productCache.set(
              handle,
              fetch(`${window.Shopify.routes.root}products/${encodeURIComponent(handle)}.js`, {
                headers: { Accept: 'application/json' },
                cache: 'no-store',
              }).then((response) => (response.ok ? response.json() : null))
            );
          }

          const product = await productCache.get(handle);
          const variant = product &&
            (product.variants || []).find((candidate) => Number(candidate.id) === Number(item.variant_id));
          return variant && variant.available ? null : item;
        } catch (error) {
          // A temporary network failure should not remove a valid line.
          return null;
        }
      })
    );

    return checks.filter(Boolean);
  }

  async function removeItems(items) {
    const updates = {};
    items.forEach((item) => {
      if (item && item.key) updates[item.key] = 0;
    });

    if (!Object.keys(updates).length) return null;

    return requestJson(`${window.Shopify.routes.root}cart/update.js`, {
      updates,
      ...sectionRequest(),
    });
  }

  async function performRewardSync() {
    try {
      if (window.kuuraiiCartPendingPromise) await window.kuuraiiCartPendingPromise;
      const cart = await fetchCart();
      const retiredGifts = (cart.items || []).filter(isRetiredGift);
      const response = await removeItems(retiredGifts);
      renderCart(response);
      if (response && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'kuuraii-offer-cleanup', cartData: response });
      }
      return true;
    } catch (error) {
      console.error('Kuuraii cart cleanup:', error);
      return false;
    }
  }

  function syncRewards() {
    if (activeSync) return activeSync;

    activeSync = performRewardSync()
      .catch((error) => {
        console.error('Kuuraii cart rewards:', error);
        showCartMessage(window.kuuraiiPrepaidStrings?.cleanupError || 'Please refresh your cart and try again.');
        return false;
      })
      .finally(() => {
        activeSync = null;
      });

    return activeSync;
  }

  function scheduleSync(delay = 180) {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncRewards, delay);
  }

  window.kuuraiiSyncCartRewards = async function () {
    if (window.kuuraiiCartPendingPromise) {
      try {
        await window.kuuraiiCartPendingPromise;
      } catch (e) {
        console.warn('Pending cart operation error before reward sync:', e);
      }
    }
    window.clearTimeout(syncTimer);
    return syncRewards();
  };

  async function checkoutPreflight(event) {
    // Only intercept native checkout button when GoKwik/Paytring is not handling it
    const checkout = event.target && event.target.closest
      ? event.target.closest('#CartDrawer-Checkout, #checkout, [name="checkout"]')
      : null;

    if (!checkout || replayingCheckout || checkoutPreflightRunning || checkout.disabled) return;
    if (checkout.classList.contains('gokwik-marge') || checkout.dataset.gokwikFunction || checkout.id === 'magic-checkout-button') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutPreflightRunning = true;
    checkout.setAttribute('aria-busy', 'true');
    checkout.disabled = true;
    showCartMessage('');

    try {
      const rewardsReady = await syncRewards();
      if (!rewardsReady) throw new Error('Reward sync failed');

      const cart = await fetchCart();
      const unavailable = await unavailableCartItems(cart);

      if (unavailable.length) {
        const response = await removeItems(unavailable);
        renderCart(response);

        const names = [
          ...new Set(unavailable.map((item) => item.product_title || item.title).filter(Boolean)),
        ];
        const itemWord = names.length === 1 ? 'item is' : 'items are';
        showCartMessage(
          `${names.join(', ')} ${itemWord} no longer available and ${
            names.length === 1 ? 'has' : 'have'
          } been removed. Please proceed to checkout again.`
        );
        scheduleSync(0);
        return;
      }

      const currentCheckout = document.getElementById(checkout.id) || document.querySelector('[name="checkout"]');
      if (!currentCheckout) throw new Error('Checkout button was not found');

      replayingCheckout = true;
      currentCheckout.disabled = false;
      currentCheckout.removeAttribute('aria-busy');
      currentCheckout.click();
      replayingCheckout = false;
    } catch (error) {
      console.error('Kuuraii checkout preflight:', error);
      showCartMessage('We could not prepare your cart for checkout. Please refresh and try again.');
    } finally {
      checkoutPreflightRunning = false;
      const currentCheckout = document.getElementById(checkout.id) || document.querySelector('[name="checkout"]');
      if (currentCheckout) {
        currentCheckout.disabled = false;
        currentCheckout.removeAttribute('aria-busy');
      }
      replayingCheckout = false;
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    const drawer = getDrawer();
    scheduleSync(0);

    const cartTrigger = document.getElementById('cart-icon-bubble');
    if (cartTrigger) cartTrigger.addEventListener('click', () => scheduleSync(0));

    document.addEventListener('click', checkoutPreflight, true);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => scheduleSync(0));
    }

    const observer = new MutationObserver(() => {
      if (!activeSync && drawer && drawer.classList.contains('active')) scheduleSync();
    });
    if (drawer) observer.observe(drawer, { childList: true, subtree: true });

    window.addEventListener('pageshow', () => scheduleSync(0));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') scheduleSync(0);
    });
  });
})();
